import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import RouteContent from '@/components/routes/route-content';
import useGetRouteList from '@/components/routes/route-list/use-get-route-list';
import RouteListStickyContent from '@/components/routes/route-list-sticky-content';
import StatusMessage from '@/components/shared/status-message';
import VirtualizedList from '@/components/ui/virtualized-list';
import useRouteSearch from '@/hooks/use-route-search';
import { ERROR_FEEDBACK, ROUTE_LIST } from '@/utils/constants';
import {
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_SORT_BY,
} from '@/utils/constants/queries';

const RouteList: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { activeSearchKey } = useRouteSearch(ROUTE_LIST);

  const currentShowParam = searchParams.get(SEARCH_PARAM_SHOW);
  const routeType = searchParams.get(SEARCH_PARAM_ROUTE_TYPE);
  const sortBy = searchParams.get(SEARCH_PARAM_SORT_BY);
  const selectedDate = searchParams.get(SEARCH_PARAM_SELECTED_DATE);

  const { isRouteLoading, journeys, hasMore, loadNextPage, isLoadingMore, isError, refetch } =
    useGetRouteList(activeSearchKey);

  const errorView = (
    <div className="min-h-[60vh] flex items-center justify-center py-8">
      <StatusMessage
        type="error"
        title={t(ERROR_FEEDBACK.general)}
        description={t(ERROR_FEEDBACK.routes.api)}
        showReloadButton
        onRetry={refetch}
      />
    </div>
  );

  const emptyView = (
    <div className="min-h-[60vh] flex items-center justify-center py-8">
      <StatusMessage
        type="empty"
        title={t(ERROR_FEEDBACK.routes.empty)}
        description={t(ERROR_FEEDBACK.routes.description)}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      <div className="shrink-0 px-4 pb-4">
        <RouteListStickyContent />
      </div>

      <VirtualizedList
        data={journeys}
        isLoading={isRouteLoading}
        isLoadingMore={isLoadingMore}
        isError={isError}
        hasMore={hasMore}
        loadNextPage={loadNextPage}
        memoryKey={currentShowParam}
        resetDependencies={[activeSearchKey, currentShowParam, routeType, sortBy, selectedDate]}
        emptyView={emptyView}
        errorView={errorView}
        renderItem={(journey) => <RouteContent key={journey.id} journey={journey} />}
      />
    </div>
  );
};

export default RouteList;
